export const FIELD_NAME = 'FIELD_NAME' as const;
export const DATA_TYPE = 'DATA_TYPE' as const;
/** Chose minimum of 1 to display backfill because otherwise setting time to "now" will always have backfill >= 1 which becomes irritiating */
export const MIN_IS_BACKFILL_COUNT = 1;
/** Warning threshold is somewhat arbitrarily chosen through discussion with MD/DW team -- less than days in month but slightly more than hours in day. */
export const WARNING_BACKFILL_COUNT = 25;
/** DJS imposes hard limit of 3K backfills: https://w.amazon.com/bin/view/DJS/UserDocs/Backfill#Troubleshooting */
export const MAX_BACKFILL_COUNT = 3_000;

export const Operation = {
	update: 'Update',
	pause: 'Pause',
	resume: 'Resume',
	activate: 'Activate',
	rerun: 'Rerun',
	revert: 'Revert',
	upload_confidence_file: 'Upload confidence file',
} as const;

export const PipelineActions = {
	...Operation,
	delete: 'Delete',
} as const;

export const ActivityActions = {
	...Operation,
	copy: 'Copy',
	delete: 'Delete', // delete is a reserved word
	create: 'Create',
} as const;

export const ActivityFormActions = {
	// Instead of displaying "Edit" as the primary action when editing a resource, instead show "Save" to make clear that it is persisting the changes
	edit: 'Save',
	copy: 'Copy',
	create: 'Create',
} as const;

export const ClearanceLevel = {
	OWNER: 'Owner',
	EDIT: 'Edit',
	READ: 'Read',
	SUBSIDIARY_GROUP: 'Subsidiary group',
	CROSSGROUP_SHARED: 'Cross-group shared',
	ACCESS_DENIED: 'Access denied',
} as const;

export const ActivityType = {
	EXTRACT: 'Extract',
	TRANSFORM: 'Transform',
	COPY: 'Load',
} as const;

export const StatusIdToCode = {
	1: 'CREATED',
	2: 'DELETED',
	3: 'RUNNING',
	4: 'ENQUEUED',
	5: 'FAILURE',
	6: 'SUCCESS',
	7: 'NEW',
	8: 'EDIT_PENDING',
	9: 'DELETE_PENDING',
	10: 'ACTIVATE_PENDING',
	11: 'SCHEDULED',
	12: 'DISCOVERED',
	13: 'PROCESSED',
	14: 'PAUSED',
	15: 'DEACTIVATED',
	16: 'SUBMITTED',
	17: 'ENABLED',
	18: 'DISABLED',
	19: 'READONLY',
	20: 'MIGRATION_ERROR',
	21: 'COMPLETED',
	22: 'ONHOLD',
	23: 'LOCKED',
	24: 'UNLOCKED',
	25: 'REJECTED',
	26: 'CANCELLED',
} as const;

export const StatusCode: { [key in (typeof StatusIdToCode)[keyof typeof StatusIdToCode]]: string } = {
	CREATED: 'Created',
	DELETED: 'Deleted',
	RUNNING: 'Running',
	ENQUEUED: 'Enqueued',
	FAILURE: 'Failure',
	SUCCESS: 'Success',
	NEW: 'New',
	EDIT_PENDING: 'Edit pending',
	DELETE_PENDING: 'Delete pending',
	ACTIVATE_PENDING: 'Activate pending',
	SCHEDULED: 'Scheduled',
	DISCOVERED: 'Discovered',
	PROCESSED: 'Processed',
	PAUSED: 'Paused',
	DEACTIVATED: 'Deactivated',
	SUBMITTED: 'Submitted',
	ENABLED: 'Enabled',
	DISABLED: 'Disabled',
	READONLY: 'Read only',
	MIGRATION_ERROR: 'Migration error',
	COMPLETED: 'Completed',
	ONHOLD: 'On hold',
	LOCKED: 'Locked',
	UNLOCKED: 'Unlocked',
	REJECTED: 'Rejected',
	CANCELLED: 'Cancelled',
} as const;

//From: https://w.amazon.com/bin/view/HammerstoneAPIUserGuideTemp/Documentations/APIs#HGetActivityInfoResponseModel
export const DjsExecutionStatus = {
	...StatusCode,
	WAITING_DEPENDENCIES: 'Waiting for preconditions',
	WaitingToBePolled: 'Waiting to be polled',
	ErrorCancellation: 'Error: Cancellation',
	DependencyTimeout: 'Dependency timeout',
	S3ConfigurationException: 'S3 configuration exception',
	ConfigurationException: 'Configuration exception',
	AgentPolledExecution: 'Agent polled execution',
	null: 'null',
} as const;

//Available Hammerstone regions, according to https://w.amazon.com/bin/view/HammerstoneAPIUserGuideTemp/Documentations/APIs#HGetActivityInfoResponseModel
export const Region = {
	'us-east-1': 'US East (N. Virginia)',
	'us-east-2': 'US East (Ohio)',
	'us-west-1': 'US West (N. California)',
	'us-west-2': 'US West (Oregon)',
	'us-iso-east-1': 'US ISO East',
	'us-isob-east-1': 'US ISOB East (Ohio)',
	'us-gov-east-1': 'AWS GovCloud (US-East)',
	'us-gov-west-1': 'AWS GovCloud (US-West)',
	'eu-west-1': 'Europe (Ireland)',
	'eu-west-2': 'Europe (London)',
	'eu-west-3': 'Europe (Paris)',
	'eu-north-1': 'Europe (Stockholm)',
	'eu-central-1': 'Europe (Frankfurt)',
	'ap-northeast-1': 'Asia Pacific (Tokyo)',
	'ap-northeast-2': 'Asia Pacific (Seoul)',
	'ap-northeast-3': 'Asia Pacific (Osaka)',
	'ap-southeast-1': 'Asia Pacific (Singapore)',
	'ap-southeast-2': 'Asia Pacific (Sydney)',
	'ap-south-1': 'Asia Pacific (Mumbai)',
	'cn-north-1': 'China (Beijing)',
	'cn-northwest-1': 'China (Ningxia)',
	'ca-central-1': 'Canada (Central)',
	'sa-east-1': 'South America (Sao Paulo)',
} as const;

export const Severity = {
	2: '2 - Bus. Crit. Function Impaired',
	6: '2.5 - Daytime Sev 2',
	3: '3 - Group Productivity Impaired',
	4: '4 - Individual Productivity Impaired',
	5: '5 - Productivity Not Immediately Affected',
} as const;

export const ActivityPriority = {
	1: '1 - Critical',
	2: '2 - High',
	3: '3 - Normal',
	4: '4 - Low',
} as const;

export const NotifyOnStatus = {
	SUCCESS: 'Success',
	RETRY: 'Retry',
	FAIL: 'Failure',
} as const;

export const SNSMessageOptions = {
	group: 'Show Group',
	pipeline: 'Pipeline Info',
	'activity type': 'Activity Type',
	'activity name': 'Activity Name',
	'activity id': 'Activity ID',
	status: 'Execution Status',
	'instance id': 'Instance ID',
	date: 'Scheduled Date',
	'updated by': 'Updated By',
	cluster: 'Cluster',
	priority: 'Priority',
	'retry active': 'Auto Retry',
	'custom message': 'Custom Message',
} as const;

export const SNSMessageOptionGroups = {
	'Default information': ['group', 'pipeline', 'activity type', 'activity name', 'activity id', 'status'],
	'Additional information': ['instance id', 'date', 'updated by', 'cluster', 'priority', 'retry active'],
	Custom: ['custom message'],
};

export const ScheduleType = {
	HOURS: 'Hour(s)',
	DAYS: 'Day(s)',
	WEEKS: 'Week(s)',
	MONTHS: 'Month(s)',
	ONETIME: 'Run once',
} as const;
export const MAX_PRECON_WAIT_MINUTES = 24 * 60;
export const MAX_PRECON_RETRYABLE_WAIT_MINUTES = 72 * 60;

/**
 * The `value` coincides with the index of the object in the array and corresponds to the `PRECON_WAIT` * in the S3 Activity Configuration metadata file.
 * See backend code how PRECON_WAIT saved in DJS for reference: https://tiny.amazon.com/rgcc1l34/codeamazpackAWSDblobb3c6src
 */
export const WaitForDependencyTimeOptions = [
	{
		label: '5 mins',
		value: 0,
		timeInMin: 5,
	},
	{
		label: '10 mins',
		value: 1,
		timeInMin: 10,
	},
	{
		label: '15 mins',
		value: 2,
		timeInMin: 15,
	},
	{
		label: '20 mins',
		value: 3,
		timeInMin: 20,
	},
	{
		label: '25 mins',
		value: 4,
		timeInMin: 25,
	},
	{
		label: '30 mins',
		value: 5,
		timeInMin: 30,
	},
	{
		label: '35 mins',
		value: 6,
		timeInMin: 35,
	},
	{
		label: '40 mins',
		value: 7,
		timeInMin: 40,
	},
	{
		label: '45 mins',
		value: 8,
		timeInMin: 45,
	},
	{
		label: '50 mins',
		value: 9,
		timeInMin: 50,
	},
	{
		label: '55 mins',
		value: 10,
		timeInMin: 55,
	},
	{
		label: '60 mins',
		value: 11,
		timeInMin: 60,
	},
	{
		label: '1 hr 10 mins',
		value: 12,
		timeInMin: 70,
	},
	{
		label: '1 hr 20 mins',
		value: 13,
		timeInMin: 80,
	},
	{
		label: '1 hr 30 mins',
		value: 14,
		timeInMin: 90,
	},
	{
		label: '1 hr 40 mins',
		value: 15,
		timeInMin: 100,
	},
	{
		label: '1 hr 50 mins',
		value: 16,
		timeInMin: 110,
	},
	{
		label: '2 hrs',
		value: 17,
		timeInMin: 120,
	},
	{
		label: '2 hrs 15 mins',
		value: 18,
		timeInMin: 135,
	},
	{
		label: '2 hrs 30 mins',
		value: 19,
		timeInMin: 150,
	},
	{
		label: '2 hrs 45 mins',
		value: 20,
		timeInMin: 165,
	},
	{
		label: '3 hrs',
		value: 21,
		timeInMin: 180,
	},
	{
		label: '4 hrs',
		value: 22,
		timeInMin: 240,
	},
	{
		label: '5 hrs',
		value: 23,
		timeInMin: 300,
	},
	{
		label: '6 hrs',
		value: 24,
		timeInMin: 360,
	},
	{
		label: '7 hrs',
		value: 25,
		timeInMin: 420,
	},
	{
		label: '8 hrs',
		value: 26,
		timeInMin: 480,
	},
	{
		label: '9 hrs',
		value: 27,
		timeInMin: 540,
	},
	{
		label: '10 hrs',
		value: 28,
		timeInMin: 600,
	},
	{
		label: '11 hrs',
		value: 29,
		timeInMin: 660,
	},
	{
		label: '12 hrs',
		value: 30,
		timeInMin: 720,
	},
	{
		label: '13 hrs',
		value: 31,
		timeInMin: 780,
	},
	{
		label: '14 hrs',
		value: 32,
		timeInMin: 840,
	},
	{
		label: '15 hrs',
		value: 33,
		timeInMin: 900,
	},
	{
		label: '16 hrs',
		value: 34,
		timeInMin: 960,
	},
	{
		label: '17 hrs',
		value: 35,
		timeInMin: 1020,
	},
	{
		label: '18 hrs',
		value: 36,
		timeInMin: 1080,
	},
	{
		label: '19 hrs',
		value: 37,
		timeInMin: 1140,
	},
	{
		label: '20 hrs',
		value: 38,
		timeInMin: 1200,
	},
	{
		label: '21 hrs',
		value: 39,
		timeInMin: 1260,
	},
	{
		label: '22 hrs',
		value: 40,
		timeInMin: 1320,
	},
	{
		label: '23 hrs',
		value: 41,
		timeInMin: 1380,
	},
	{
		label: '1 day',
		value: 42,
		timeInMin: 1440,
	},
	{
		label: '1 day 2 hrs',
		value: 43,
		timeInMin: 1560,
	},
	{
		label: '1 day 4 hrs',
		value: 44,
		timeInMin: 1680,
	},
	{
		label: '1 day 6 hrs',
		value: 45,
		timeInMin: 1800,
	},
	{
		label: '1 day 8 hrs',
		value: 46,
		timeInMin: 1920,
	},
	{
		label: '1 day 10 hrs',
		value: 47,
		timeInMin: 2040,
	},
	{
		label: '1 day 12 hrs',
		value: 48,
		timeInMin: 2160,
	},
	{
		label: '1 day 14 hrs',
		value: 49,
		timeInMin: 2280,
	},
	{
		label: '1 day 16 hrs',
		value: 50,
		timeInMin: 2400,
	},
	{
		label: '1 day 18 hrs',
		value: 51,
		timeInMin: 2520,
	},
	{
		label: '1 day 20 hrs',
		value: 52,
		timeInMin: 2640,
	},
	{
		label: '1 day 22 hrs',
		value: 53,
		timeInMin: 2760,
	},
	{
		label: '2 days',
		value: 54,
		timeInMin: 2880,
	},
	{
		label: '2 days 12 hrs',
		value: 55,
		timeInMin: 3600,
	},
	{
		label: '3 days',
		value: 56,
		timeInMin: 4320,
	},
] as const;

export const CompareOperator = {
	GE: 'Greater or Equal (≥)',
	LE: 'Less or Equal (≤)',
	EQ: 'Equals (=)',
	'NOT EQ': 'Not Equals (≠)',
	BETWEEN: 'Between (><)',
	'NOT BETWEEN': 'Not Between (<>)',
} as const;

export const PreconditionS3Type = {
	s3Key: 'S3 Key',
	s3Prefix: 'S3 Prefix',
} as const;

export const TransformParamType = {
	SQL: 'SQL',
	LIST: 'List',
	RANGE: 'Range',
	FREE_FORM: 'Free Form',
} as const;

export const TransformSQLType = {
	INSERT: 'Insert',
	UPDATE: 'Update',
} as const;

export const LoadReplaceOption = {
	INSERT: 'Insert',
	UPSERT: 'Upsert',
} as const;

export const LoadSourceType = {
	FILE: 'File',
	MANIFEST: 'Manifest',
} as const;

export const TargetDBType = {
	REDSHIFT: 'Redshift',
} as const;

export const LoadFileFormat = {
	DELIMITER: 'Delimiter',
	CSV: 'CSV',
	JSON: 'JSON',
	PARQUET: 'Parquet',
	CUSTOM: 'Custom',
} as const;

//NOTE: Delimiters is NOT exhaustive
export const Delimiters = {
	',': 'comma',
	'\t': 'tab',
	'\n': 'line-break',
	'|': 'pipe',
	':': 'colon',
	';': 'semi-colon',
	' ': 'space',
	'~': 'tilda',
	'': 'empty string',
} as const;

/**
 * S3 Credential types are an enum return from Hammerstone API. The corresopnding declarations can be found in...
 * Lambda: https://code.amazon.com/packages/AWSHammerstoneExposedRESTfulServiceLambda/blobs/76e7210be18dada9331ab2c4e0f6e1b89db25fa0/--/src/com/amazon/awshammerstoneexposedrestfulservicelambda/constant/Constant.java#L66-L70
 * Coral Model: https://code.amazon.com/packages/AWSHammerstoneExposedRESTfulServiceLambdaModel/blobs/b4777df9bee4f2e3487d91b9cc645b0942384b04/--/model/Datatypes.xml#L634-L645
 */
export const S3CredentialType = {
	IAM: 'IAM',
	ODIN: 'Odin (deprecated)',
} as const;

export const preconditionWaitforDependencyLabel = 'Wait for dependencies';
